import { NavLink } from 'react-router-dom';
import { Nav } from 'react-bootstrap';
import { useAuth } from '@/contexts/AuthContext';
import { useUi } from '@/contexts/UiContext';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Only highlight on an exact path match (for parent routes such as /dashboard). */
  end?: boolean;
}

const dashboardItems: NavItem[] = [
  { to: '/dashboard', label: 'Overview', icon: 'bi-grid-1x2', end: true },
  { to: '/dashboard/brand-performance', label: 'Brand Performance', icon: 'bi-speedometer2' },
  { to: '/dashboard/brand-deep-dive', label: 'Brand Deep Dive', icon: 'bi-zoom-in' },
  { to: '/dashboard/content-mix', label: 'Content Mix', icon: 'bi-collection' },
  { to: '/dashboard/offer-strategy', label: 'Offer Strategy', icon: 'bi-tags' },
  { to: '/dashboard/strategic-intent', label: 'Strategic Intent', icon: 'bi-bullseye' },
  { to: '/dashboard/send-timing', label: 'Send Timing', icon: 'bi-clock-history' },
  { to: '/dashboard/persona-performance', label: 'Persona Performance', icon: 'bi-people' },
  { to: '/dashboard/monthly-cadence', label: 'Monthly Cadence', icon: 'bi-calendar3' },
  { to: '/dashboard/hourly-cadence', label: 'Hourly Cadence', icon: 'bi-alarm' },
  { to: '/comparison', label: 'Comparison', icon: 'bi-arrows-angle-contract' },
  { to: '/message-explorer', label: 'Message Explorer', icon: 'bi-chat-left-dots' },
];

const aiItems: NavItem[] = [
  { to: '/ai/suggestions', label: 'AI Suggestions', icon: 'bi-lightbulb' },
  { to: '/ai/prompting', label: 'AI Prompting', icon: 'bi-chat-dots' },
];

/** Team + user management — crm:editor and above (editors get a delegated view). */
const managementItems: NavItem[] = [
  { to: '/admin/teams', label: 'Teams', icon: 'bi-people-fill' },
  { to: '/admin/users', label: 'User Management', icon: 'bi-person-gear' },
];

function SidebarSection({
  title,
  items,
  collapsed,
}: {
  title: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  return (
    <>
      {!collapsed && title.length > 0 && (
        <div className="text-uppercase small fw-semibold text-secondary px-3 mt-3 mb-1">
          {title}
        </div>
      )}
      {items.map((item) => (
        <Nav.Item key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `nav-link d-flex align-items-center gap-2 px-3 py-2 sidebar-link ${isActive ? 'active' : ''}`
            }
            title={item.label}
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            {!collapsed && (
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {item.label}
              </span>
            )}
          </NavLink>
        </Nav.Item>
      ))}
    </>
  );
}

export function Sidebar() {
  const { isAnalystOrAbove, isEditorOrAbove } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useUi();

  return (
    <aside className={`app-sidebar d-flex flex-column ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="app-sidebar-header d-flex align-items-center justify-content-between px-3 py-3">
        {!sidebarCollapsed && (
          <div className="text-uppercase small fw-semibold text-secondary">Dashboards</div>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline-light border-0"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i
            className={`bi ${sidebarCollapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'}`}
          />
        </button>
      </div>
      <Nav className="flex-column flex-grow-1 sidebar-nav">
        <SidebarSection title="" items={dashboardItems} collapsed={sidebarCollapsed} />
        {isAnalystOrAbove && (
          <SidebarSection title="AI Tools" items={aiItems} collapsed={sidebarCollapsed} />
        )}
        {isEditorOrAbove && (
          <SidebarSection title="Management" items={managementItems} collapsed={sidebarCollapsed} />
        )}
      </Nav>
    </aside>
  );
}
